import { useState, useMemo, useRef, useEffect } from "react";
import {
  BookOpen, Search, ChevronRight, ChevronDown, Printer, Shield,
  Users, Plane, Wrench, AlertTriangle, Building, Radio, BarChart3,
  Activity, Zap, ShieldCheck, AlertOctagon, Info, CheckCircle,
  Clock, FileText, List, RefreshCw, Star, Database,
} from "lucide-react";

// ─── Manual Content ───────────────────────────────────────────────────────────
const CHAPTERS = [
  // ── 1. Introduction ─────────────────────────────────────────────────────────
  {
    id: "introduction", title: "Introduction to Fatigue Shield", icon: BookOpen,
    color: "#3b82f6",
    sections: [
      {
        id: "intro-purpose", heading: "Platform Purpose",
        content: "Fatigue Shield is a sovereign, AI-enabled aviation operations platform built for Indian scheduled carriers. It unifies fatigue risk management, crew scheduling, live flight operations, regulatory compliance, and airworthiness management across all airline departments into a single real-time intelligence platform. The platform is DGCA-native — every rule, limit, and workflow is designed around Indian aviation regulations (CAR Section 7, FTL, FDTL, CAR-M) from the ground up, not adapted from foreign frameworks."
      },
      {
        id: "intro-modules", heading: "Platform Modules", type: "table",
        headers: ["Module", "Primary Users", "DGCA Reference"],
        rows: [
          ["Enterprise Dashboard", "CEO, COO, Safety Officers", "CAR Section 7 Series M"],
          ["Crew Rostering Platform", "Crew Scheduling, Flight Ops", "CAR-FTL: FDP ≤13h, rest ≥12h"],
          ["AI Disruption Engine", "Ops Control, Crew Scheduling", "DGCA FDTL dispensation docs"],
          ["Live Flight Ops Board", "Flight Ops, Crew Control", "CAR-FTL domestic FDP rules"],
          ["Engineering Risk", "Engineering Manager, AMEs", "CAR-M + ICAO Annex 19"],
          ["CAMO Module", "CAMO Planner, Airworthiness Eng.", "CAR-M CAMO requirements"],
          ["Safety Analytics", "Safety Manager, SMS Officer", "DGCA CAR-19 + IS-BAO"],
          ["Ground Ops & Back Office", "Station Manager, HR", "CAR Section 9"],
          ["IRROPS Console", "Ops Control", "DGCA FDTL dispensation"],
          ["AI Staffing Engine", "HR, Crew Scheduling", "DGCA minimum crew ratios"],
        ]
      },
      {
        id: "intro-auth", heading: "Access Roles", type: "table",
        headers: ["Role", "Access Level", "Primary Modules"],
        rows: [
          ["admin", "Full access to all modules", "All modules + Admin Settings"],
          ["executive", "Read-only strategic view", "Dashboard, all read views"],
          ["crew_scheduling", "Full crew and roster access", "Roster, Disruption, Compliance"],
          ["pilot", "Own profile and roster view", "Crew Master (own record)"],
          ["ame", "Engineering and CAMO view", "Engineering Risk, CAMO"],
          ["engineering_manager", "Engineering and CAMO full access", "Engineering, CAMO, IRROPS"],
          ["safety_manager", "Safety and compliance access", "Safety, Compliance, Alerts"],
        ]
      },
      {
        id: "intro-fatigue-score", heading: "The Fatigue Score — 0 to 100", type: "steps",
        intro: "Every fatigue score displayed in Fatigue Shield is a real-time composite of five FRMS-aligned factors. Each sub-score is normalised to 0–100 before weighting, then summed to a final index.",
        steps: [
          { title: "Time Since Last Sleep (40%)", body: "Hours awake since last valid sleep opportunity (≥7h uninterrupted). Score rises sharply after 14h — every 2 additional hours adds ~10 risk points. Capped at 20h awake = 100 points." },
          { title: "Cumulative Duty Hours (25%)", body: "Rolling 7-day and 28-day flight hours vs. DGCA limits (60h/7d, 190h/28d). Breaching >80% of either limit adds significant risk. Prevents schedule patterns that hide chronic fatigue." },
          { title: "Circadian Disruption — WOCL (20%)", body: "Penalty for duty starting or operating through the Window of Circadian Low (WOCL: 0200–0559 local). Human alertness is at its physiological minimum during this window — each overlapping hour adds 12.5 points." },
          { title: "Rest Adequacy (10%)", body: "Actual rest received vs. DGCA minimum (12h domestic). Rest <12h triggers a deficit penalty. Full penalty applies at <9h rest. Captures whether recovery was adequate, not just legally compliant." },
          { title: "Consecutive Duty Days (5%)", body: "Days worked consecutively without a full off day. Beyond 5 consecutive duty days, a compounding factor is applied. Reflects accumulated sleep debt that individual rest periods cannot fully reverse." },
        ]
      },
      {
        id: "intro-risk-bands", heading: "Risk Level Bands", type: "table",
        headers: ["Score", "Level", "Operational Action Required"],
        rows: [
          ["0 – 25", "Fully Rested", "No restrictions — assign normally"],
          ["26 – 45", "Mild Fatigue", "Monitor — flag for next roster review"],
          ["46 – 65", "Moderate Fatigue", "Supervisor review required before assignment"],
          ["66 – 80", "High Fatigue", "Restricted duties — no extended FDP authorised"],
          ["81 – 100", "CRITICAL", "Do Not Assign — mandatory rest required immediately"],
        ]
      },
      {
        id: "intro-legal-safe-gap", heading: "The Legal vs Safe Gap", type: "warning",
        content: "A crew member may be legally compliant (FDP <13h, rest >12h) yet carry a fatigue score of 70+ due to WOCL overlap and prior cumulative hours. Fatigue Shield surfaces this gap. This is the most dangerous blind spot in compliance-only frameworks — the crew member is legal but operationally unsafe. Fatigue Shield is one of very few systems in Indian aviation that makes this gap visible."
      },
    ]
  },

  // ── 2. CAMO Module ──────────────────────────────────────────────────────────
  {
    id: "camo", title: "CAMO Module", icon: ShieldCheck, color: "#06b6d4",
    sections: [
      {
        id: "camo-purpose", heading: "Module Purpose",
        content: "The CAMO (Continuing Airworthiness Management Organisation) module manages the ongoing airworthiness obligations of the airline's fleet. It tracks three core regulatory requirements: Airworthiness Review Certificates (ARC), Minimum Equipment List (MEL) deferrals, and Aircraft Maintenance Programme (AMP) compliance. It also provides a full task management system for CAMO planners to create, assign, and close airworthiness-related work items."
      },
      {
        id: "camo-data-arch", heading: "Data Architecture: Where Do Tasks Come From?", type: "note",
        content: "Fatigue Shield supports two modes of operation for CAMO data:\n\nOPTION A — Self-Contained (Active): All tasks, ARC records, and MEL deferrals are created and managed directly within Fatigue Shield by CAMO planners. This is the current active mode. Every record shows a 'Fatigue Shield' source badge.\n\nOPTION B — MRO API Integration (Roadmap): For carriers using dedicated MRO/CAMO software (AMOS, OASES, Ramco Aviation, TRAX), Fatigue Shield can pull data automatically via API. Records synced from external systems show an 'API Sync' source badge. Both modes can coexist — planners can annotate API-synced tasks with fatigue-specific notes.\n\nCompatible MRO Systems: AMOS (Swiss-AS) — used by IndiGo, Air India; OASES — SpiceJet and smaller carriers; Ramco Aviation — Indian-built, Indian Navy; TRAX — various regional operators."
      },
      {
        id: "camo-arc-what", heading: "ARC — What It Is and Why It Matters",
        content: "The Airworthiness Review Certificate (ARC) is a legal regulatory document issued under CAR-M confirming that an aircraft continues to meet its airworthiness requirements. It is renewed annually by a qualified Airworthiness Review Staff (ARS) member. An aircraft with an expired ARC cannot legally operate revenue flights. DGCA requires a valid ARC for each aircraft at all times."
      },
      {
        id: "camo-arc-status", heading: "ARC Status Indicators", type: "table",
        headers: ["Status", "Meaning", "Action Required"],
        rows: [
          ["Valid (>90 days)", "Certificate is current with comfortable margin", "No immediate action — plan next review cycle"],
          ["Expiring Soon (31–60 days)", "Certificate expires within 2 months", "Initiate ARC review and schedule ARS inspection"],
          ["Expiring Critical (<30 days)", "Certificate expires within 30 days", "Immediate action — ARC review must be in progress"],
          ["EXPIRED", "Certificate past expiry date", "Aircraft is unairworthy — do not assign to revenue ops. Update ARC record once new certificate is issued."],
        ]
      },
      {
        id: "camo-task-vs-cert", heading: "IMPORTANT: Task Completion vs Certificate Renewal", type: "warning",
        content: "Marking an 'Airworthiness Review' CAMO task as Completed does NOT automatically update the ARC record. These are two separate things:\n\nThe TASK represents the planning work — coordinating the review, gathering documentation, scheduling the ARS inspector, liaising with DGCA. Completing the task means this work is done.\n\nThe ARC RECORD represents the actual regulatory certificate. It only changes when a new certificate is physically issued with a new ARC number, new issue date, and new expiry date. No system should ever automatically update a regulatory certificate based on a task status — that decision must be made by a qualified Airworthiness Review Staff member.\n\nWORKFLOW: When an ARC Renewal task is marked Completed, Fatigue Shield will show a prompt: 'Update ARC record for [Aircraft] →'. Click it to go to the ARC Tracking tab and enter the new certificate details. Only then will the aircraft status change from EXPIRED to Valid."
      },
      {
        id: "camo-arc-workflow", heading: "ARC Renewal — Step-by-Step Workflow", type: "steps",
        steps: [
          { title: "Create ARC Pre-Review Task", body: "In the Tasks tab, create a new task. Category: 'Airworthiness Review'. Set the aircraft tail, priority (High if <60 days), due date, and assign to the responsible planner." },
          { title: "Conduct Review", body: "The ARS member conducts the physical airworthiness review. All airworthiness documentation, maintenance records, and outstanding ADs/SBs must be reviewed and found satisfactory." },
          { title: "Mark Task Completed", body: "Once the review and all documentation is complete, mark the task as 'Completed' in the Tasks tab. The system will show a prompt to update the ARC record." },
          { title: "Update ARC Record", body: "Navigate to ARC Tracking tab (or click the prompt). Use the edit (pencil) icon on the aircraft's row. Enter the new ARC number, new issue date, and new expiry date from the physical certificate." },
          { title: "Verify Status Change", body: "After saving, verify the aircraft's ARC status has changed from EXPIRED to Valid. The days-to-expiry counter should now show a positive number based on the new expiry date." },
          { title: "Notify Stakeholders", body: "The updated ARC status is immediately visible on the fleet matrix in the CAMO Overview and in the Enterprise Dashboard fleet readiness view." },
        ]
      },
      {
        id: "camo-mel-categories", heading: "MEL — Category Definitions", type: "table",
        headers: ["Category", "Maximum Deferral Period", "Meaning", "Example"],
        rows: [
          ["A", "Before next flight / As specified", "Item must be rectified before the next flight departure. The MEL procedure specifies the exact condition.", "Engine fire suppression system fault"],
          ["B", "3 calendar days", "Item must be rectified within 3 days of the deferral. Day 0 = day of deferral.", "Weather radar test function inoperative"],
          ["C", "10 calendar days", "Item must be rectified within 10 days of the deferral.", "Cabin temperature zone controller degraded"],
          ["D", "120 calendar days", "Item may be deferred for up to 120 days. Cosmetic or low-criticality items.", "Passenger reading light inoperative"],
        ]
      },
      {
        id: "camo-mel-workflow", heading: "MEL Deferral — Step-by-Step Workflow", type: "steps",
        steps: [
          { title: "Identify Inoperative Item", body: "During pre-flight or maintenance, an item is found inoperative. The AME checks the aircraft MEL to confirm the item can be deferred (not all items can be in the MEL)." },
          { title: "Determine Category", body: "The AME identifies the MEL item reference number, ATA chapter, and the category (A/B/C/D) from the approved MEL document. The category determines how many days the aircraft can fly with this item deferred." },
          { title: "Create MEL Deferral", body: "In Fatigue Shield CAMO → MEL Deferrals tab → New Deferral. Enter: aircraft tail, MEL item reference, ATA chapter, description, category, deferral date, and the name/licence number of the deferring AME. The system automatically calculates the expiry date." },
          { title: "Placard and Proceed", body: "The inoperative item must be placarded in the aircraft as per MEL requirements. The aircraft can then be dispatched with the deferral active." },
          { title: "Monitor Expiry", body: "Fatigue Shield shows days remaining for each active deferral with colour coding: green (plenty of time), amber (<10 days), red (expired or overdue). A CAMO task can be linked to track the rectification work." },
          { title: "Clear the Deferral", body: "Once the item is repaired and the AME certifies the work, click 'Clear' on the MEL deferral record. Enter the name of the clearing AME. The record status changes to 'Cleared' and is retained for audit purposes." },
        ]
      },
      {
        id: "camo-amp", heading: "AMP — Aircraft Maintenance Programme Compliance",
        content: "The AMP Compliance tab tracks flight-hour-based maintenance checks for each aircraft in the fleet. Each check has a defined interval in flight hours (FH). The system shows the last performed FH, current total FH, next due FH, and the FH remaining before the check is due.\n\nStatus indicators:\nCurrent — FH remaining >100 hours. No immediate action.\nDue Soon — FH remaining ≤100 hours. Schedule the check.\nOverdue — Next due FH exceeded. Aircraft should not fly until check is completed.\n\nIn the current implementation, AMP data is updated manually or via integration with the MRO system (Option B). In a live environment, flight hours are automatically updated from the Aircraft Technical Log (ATL) system."
      },
      {
        id: "camo-dgca", heading: "DGCA Regulatory References", type: "table",
        headers: ["Regulation", "Relevance"],
        rows: [
          ["CAR-M (Continuing Airworthiness)", "Primary regulatory framework for CAMO operations, ARC, and maintenance organisations"],
          ["CAR-M Subpart C — Continuing Airworthiness", "Requirements for ARC issue, validity, and renewal procedures"],
          ["CAR-M Subpart B — Responsibilities", "CAMO accountability and responsibilities per aircraft managed"],
          ["Airworthiness Notice No. 1", "DGCA ARC procedures for Indian-registered aircraft"],
          ["EASA AMC M.A.901", "Technical guidance for ARC surveys (referenced by DGCA)"],
        ]
      },
    ]
  },

  // ── 3. Crew Rostering ───────────────────────────────────────────────────────
  {
    id: "crew-rostering", title: "Crew Rostering Platform", icon: Users, color: "#8b5cf6",
    sections: [
      {
        id: "cr-purpose", heading: "Module Purpose",
        content: "The Crew Rostering Platform is an 8-module integrated suite that manages the complete lifecycle of crew assignment — from profile management to legal compliance checking. It enforces DGCA Flight Time Limitations (FTL) and Flight Duty Period (FDP) rules automatically at every step, preventing illegal assignments before they are committed."
      },
      {
        id: "cr-fdp", heading: "FDP and Rest Rules — DGCA CAR-FTL", type: "table",
        headers: ["Rule", "Domestic Limit", "Consequence if Breached"],
        rows: [
          ["Maximum FDP", "13 hours", "System blocks assignment — crew cannot be assigned"],
          ["Minimum rest after FDP", "12 hours", "Next assignment cannot begin until rest period satisfied"],
          ["Maximum flight time per 7 days", "60 hours", "Compliance Board flags and blocks further assignments"],
          ["Maximum flight time per 28 days", "190 hours", "Cumulative limit enforced by Compliance Board"],
          ["Maximum flight time per year", "900 hours", "Annual limit — tracked in crew profile"],
          ["WOCL duty start", "Avoid 0200–0559", "Fatigue score penalty applied — supervisor review triggered"],
        ]
      },
      {
        id: "cr-eligibility", heading: "Eligibility Engine — How Crew Are Qualified",
        content: "Before any crew member can be assigned to a flight or disruption recovery, the eligibility engine checks four criteria simultaneously:\n\n1. TYPE RATING: Does the crew member hold a valid type rating for the aircraft type? (e.g. B737 MAX, A320neo)\n2. AIRPORT CLEARANCE: Is the crew member cleared for the departure and arrival airports? Certain airports require special authorisation (high altitude, short runway, challenging approaches).\n3. TRAINING CURRENCY: Are all mandatory recurrent training items (SEP, CRM, DGR) current and not expired?\n4. LICENCE VALIDITY: Is the pilot's ATPL/CPL and medical certificate current?\n\nAny crew member failing any one of these checks is marked BLOCKED with the specific reason displayed. Only crew passing all four are shown as ELIGIBLE."
      },
      {
        id: "cr-modules", heading: "The 8 Modules", type: "table",
        headers: ["Module", "Purpose"],
        rows: [
          ["Crew Master", "Full crew profiles: licences, type ratings, airport clearances, training records, contract details"],
          ["Roster Planner", "Visual Gantt chart — view and manage all crew assignments across a date range with FDP indicators"],
          ["Pairing Builder", "Build and validate flight duty sequences (pairings) from individual sectors"],
          ["Compliance Board", "Automated DGCA FTL rule checking — flags any limit breach across the roster"],
          ["Recovery Console", "Manage crew recovery from disruptions — view available replacements and activate"],
          ["Audit Log", "Full chronological record of every roster change, assignment, and user action"],
          ["Scenario Simulator", "What-if planning: model roster scenarios without affecting live operations"],
          ["Disruption Engine", "AI-powered disruption analysis — see separate chapter"],
        ]
      },
    ]
  },

  // ── 4. AI Disruption Engine ─────────────────────────────────────────────────
  {
    id: "disruption", title: "AI Disruption Engine", icon: Zap, color: "#f59e0b",
    sections: [
      {
        id: "dis-purpose", heading: "Module Purpose",
        content: "The AI Disruption Engine uses Claude AI to analyse crew disruption events and provide scored, ranked recovery options. It replaces phone-based, unstructured disruption management with a systematic, auditable, DGCA-compliant workflow that finds eligible replacement crew for both Captain and First Officer roles simultaneously."
      },
      {
        id: "dis-types", heading: "Disruption Types Handled", type: "table",
        headers: ["Category", "Examples"],
        rows: [
          ["Crew Medical", "Sick call, medical unfit, incapacitation"],
          ["FDP Breach", "Crew approaching or breaching maximum duty time"],
          ["Rest Violation", "Insufficient rest before assignment"],
          ["Diversion", "Flight diverted — crew out of base"],
          ["AOG", "Aircraft on ground — crew without aircraft"],
          ["Weather", "Weather delay causing FDP breach or rest violation"],
          ["Cascade", "Disruption caused by delay to previous flight in sequence"],
          ["Training Conflict", "Crew training event conflicts with operational assignment"],
        ]
      },
      {
        id: "dis-scoring", heading: "Claude AI 5-Dimension Scoring", type: "steps",
        intro: "When a disruption is analysed, Claude AI scores each potential recovery option across five dimensions. Each dimension is scored 0–100 and combined into an overall recommendation score.",
        steps: [
          { title: "Compliance (Legal)", body: "Will the recovery option result in any FTL, FDP, or rest violation? A non-compliant option scores 0 on this dimension regardless of other factors." },
          { title: "Fatigue Risk", body: "What is the current fatigue score of the proposed replacement crew? Lower fatigue score = higher compliance score." },
          { title: "Operational Cost", body: "Does activating this crew require repositioning, additional fees, or operational overhead? Lower cost = higher score." },
          { title: "Delay Impact", body: "How quickly can the replacement crew reach the aircraft? Faster turnaround = higher score." },
          { title: "Fairness (Quality of Life)", body: "Has this crew member been called in frequently recently? Fair distribution of disruption duties = higher score." },
        ]
      },
      {
        id: "dis-workflow", heading: "Disruption Resolution Workflow", type: "steps",
        steps: [
          { title: "Trigger Disruption", body: "A disruption event is created — either manually (Ops Controller) or automatically from the Live Flight Ops Board by clicking 'Trigger Disruption' on any live flight." },
          { title: "AI Analysis", body: "Click 'Analyse with AI'. Claude AI reviews the disruption context, checks all eligible crew, and returns a scored assessment with specific recovery options. This typically takes 5–15 seconds." },
          { title: "Review Options", body: "The AI returns 2–3 ranked recovery options, each with: recommended crew (Captain + FO), compliance check results, fatigue scores, and a plain-English operational assessment." },
          { title: "Check Eligibility", body: "For each proposed crew member, the eligibility panel shows: type rating status, airport clearances, training currency, and licence validity. Any BLOCKED crew cannot be assigned." },
          { title: "Resolve", body: "Select the preferred recovery option and click 'Resolve'. Both Captain and First Officer are simultaneously replaced in the system. The roster is updated and an audit log entry is created." },
          { title: "Verify", body: "Return to the Live Flight Ops Board or Roster Planner to confirm both crew have been updated on the flight. The disruption event is marked as resolved with timestamp and resolver name." },
        ]
      },
      {
        id: "dis-both-crew", heading: "Why Both Crew Are Replaced", type: "note",
        content: "The system always finds replacement crew for BOTH Captain and First Officer independently. This is intentional — the eligibility criteria differ by rank (Captain requires ATPL + type rating + command experience; FO requires CPL + type rating). A single disruption may have replaced only one crew member, but the other may also be approaching FDP limits or have other restrictions. The system checks both roles every time for safety assurance."
      },
    ]
  },

  // ── 5. Live Flight Ops ──────────────────────────────────────────────────────
  {
    id: "flight-ops", title: "Live Flight Ops Board", icon: Plane, color: "#22c55e",
    sections: [
      {
        id: "fo-purpose", heading: "Module Purpose",
        content: "The Live Flight Ops Board displays real-time Indian carrier flight data from AviationStack, fused with crew assignments from the roster. It provides live FDP monitoring per crew member, a geographic map of all flights, and the ability to convert any live flight into a disruption scenario for crew recovery practice or live disruption management."
      },
      {
        id: "fo-fdp", heading: "FDP Indicator Colours", type: "table",
        headers: ["Colour", "Status", "Action"],
        rows: [
          ["Green", "FDP < 70% of limit", "Normal — no action required"],
          ["Amber", "FDP 70–90% of limit", "Monitor — crew approaching limit"],
          ["Red", "FDP >90% of limit or exceeded", "Immediate attention — review assignment or initiate disruption recovery"],
        ]
      },
      {
        id: "fo-data", heading: "Data Sources and Refresh", type: "note",
        content: "Flight data is sourced from the AviationStack API and cached for 10 minutes to manage API rate limits. Crew-flight assignments are simulated — Captain and First Officer are assigned from the roster based on fleet type and base matching. In a live operations environment, crew-flight assignments would be pulled directly from the Crew Management System (CMS) integration.\n\nAirport coordinates for 34 Indian airports are stored locally in the system for map rendering. The great-circle routes shown on the Leaflet map are calculated in real-time from departure and arrival airport coordinates."
      },
    ]
  },

  // ── 6. Engineering Risk ─────────────────────────────────────────────────────
  {
    id: "engineering", title: "Engineering Risk Module", icon: Wrench, color: "#ef4444",
    sections: [
      {
        id: "eng-purpose", heading: "Module Purpose",
        content: "The Engineering Risk module extends FRMS (Fatigue Risk Management System) beyond flight crew to Aircraft Maintenance Engineers (AMEs). AMEs working night shifts, irregular hours, and on safety-critical tasks accumulate dangerous fatigue levels that are completely invisible to existing safety management systems in most Indian carriers. This module makes that fatigue visible and actionable."
      },
      {
        id: "eng-frms", heading: "Why AME Fatigue Matters", type: "warning",
        content: "ICAO Annex 19 and DGCA CAR-M both recognise that maintenance human factors — including fatigue — are a leading causal factor in maintenance errors. A fatigued AME certifying a safety-critical maintenance release is a direct safety risk equivalent to a fatigued pilot. Unlike flight crew, AMEs have no formal DGCA FTL framework — their hours are managed under general labour law. Fatigue Shield applies the same FRMS principles used for flight crew to maintenance staff, with adjustments for the nature of maintenance shift patterns."
      },
      {
        id: "eng-task-risk", heading: "Task Criticality Classification", type: "table",
        headers: ["Category", "Examples", "Action if AME Fatigue Score >65"],
        rows: [
          ["Airworthiness-Critical", "Aircraft release to service, engine run, flight control rig", "Do Not Assign — mandatory rest required"],
          ["Safety-Critical", "Fuel system maintenance, landing gear inspection, avionics", "Supervisor review and approval required before assignment"],
          ["Complex", "C-check line items, engine change, structural repair", "Flag for shift leader awareness"],
          ["Routine", "Oil replenishment, tyre pressure check, cabin cleaning", "Monitor — document fatigue level in shift log"],
        ]
      },
    ]
  },

  // ── 7. Safety Analytics ─────────────────────────────────────────────────────
  {
    id: "safety", title: "Safety Analytics", icon: AlertTriangle, color: "#f97316",
    sections: [
      {
        id: "saf-purpose", heading: "Module Purpose",
        content: "Safety Analytics cross-references SMS (Safety Management System) reports with operational fatigue data. The goal is to identify whether fatigue was a causal or contributing factor in safety events, and to flag crew or staff who appear on both the high-fatigue list and the recent SMS report list."
      },
      {
        id: "saf-correlation", heading: "Fatigue-Safety Correlation", type: "note",
        content: "The fatigue-safety correlation engine works by:\n1. Reviewing all SMS reports where the event date/time overlaps with a high-fatigue period for the crew or AME involved.\n2. Calculating a correlation coefficient between cumulative fatigue scores and SMS report frequency per department.\n3. Identifying individuals who appear on both the 'High Fatigue' list and the 'Recent SMS Reporter' list within the same 30-day period.\n\nThis is a statistical tool — correlation is not causation. All flagged events require human review by the Safety Manager before any operational action is taken."
      },
      {
        id: "saf-sms-categories", heading: "SMS Report Categories", type: "table",
        headers: ["Category", "Description"],
        rows: [
          ["Air Safety", "In-flight incidents, near-misses, ATC events"],
          ["Ground Safety", "Ground handling incidents, FOD, vehicle conflicts"],
          ["Maintenance Safety", "Maintenance errors, incorrect procedures, tooling issues"],
          ["Cabin Safety", "Passenger incidents, cabin crew events, medical events"],
          ["Security", "Unauthorised access, unruly passengers, security breaches"],
          ["Hazardous Materials", "DGR handling incidents, spills, incorrect loading"],
          ["Fatigue Report", "Crew-reported fatigue events — self-declarations"],
          ["Near Miss", "Events that could have resulted in an accident"],
        ]
      },
    ]
  },

  // ── 8. IRROPS Console ──────────────────────────────────────────────────────
  {
    id: "irrops", title: "IRROPS Console", icon: Radio, color: "#ec4899",
    sections: [
      {
        id: "irr-purpose", heading: "Module Purpose",
        content: "The IRROPS (Irregular Operations) Console provides a structured digital command layer for managing any deviation from the published schedule. It replaces unstructured phone-based IRROPS management with an auditable, priority-classified event log that captures every decision and its regulatory basis."
      },
      {
        id: "irr-priority", heading: "Alert Priority Levels", type: "table",
        headers: ["Priority", "Trigger", "Response Time Target"],
        rows: [
          ["P1 — Critical", "FDP breach, AOG, diversion in progress, crew incapacitation", "Immediate — within 5 minutes"],
          ["P2 — High", "FDP approaching 90% limit, rest period conflict, crew unavailable", "Within 15 minutes"],
          ["P3 — Medium", "Schedule delay >1 hour, crew repositioning required", "Within 30 minutes"],
          ["P4 — Low", "Minor delay, informational updates, roster advisory", "Monitor only"],
        ]
      },
      {
        id: "irr-legal", heading: "Regulatory Documentation During IRROPS", type: "warning",
        content: "During IRROPS operations, DGCA requires that any FDP extension beyond the standard limit be documented with a dispensation record, crew consent, and medical confirmation if applicable. The IRROPS Console creates an automatic audit trail of every decision and its timestamp. This documentation is essential for any DGCA inquiry or incident investigation following an IRROPS event. All events, crew notified, decisions made, and approvals obtained must be recorded in the system before or immediately after the IRROPS event concludes."
      },
    ]
  },

  // ── 9. AI Staffing Engine ───────────────────────────────────────────────────
  {
    id: "staffing", title: "AI Staffing Engine", icon: BarChart3, color: "#a855f7",
    sections: [
      {
        id: "stf-purpose", heading: "Module Purpose",
        content: "The AI Staffing Engine provides forward-looking crew demand forecasts that account for operational growth, seasonal traffic patterns, training pipeline lead times, and regulatory attrition requirements. It goes significantly beyond the simplistic crew ratio (e.g. 10 crew per aircraft) used by most Indian carriers, by modelling the actual availability pipeline."
      },
      {
        id: "stf-variables", heading: "Key Variables Modelled", type: "table",
        headers: ["Variable", "Why It Matters"],
        rows: [
          ["Fleet Growth Plan", "Each new aircraft requires 2 Captains + 2 FOs minimum before it can operate"],
          ["Type Rating Pipeline", "Type rating training takes 6–12 weeks per pilot — this lead time must be built into hiring plans"],
          ["Recurrency Calendar", "Pilots on annual simulator recurrency (SEP, Instrument Rating) are unavailable for 2–5 days each"],
          ["Attrition Rate", "Historical attrition + projected promotions reduce available headcount continuously"],
          ["Seasonal Traffic", "Peak season (Oct–Jan, Apr–May) requires 15–25% more crew availability than off-peak"],
          ["Leave Entitlement", "DGCA mandates annual leave — crew on leave are not operationally available"],
        ]
      },
      {
        id: "stf-gap", heading: "Reading the Gap Analysis", type: "note",
        content: "The Staffing Engine output shows three lines per fleet and rank:\n1. REQUIRED: The number of available (trained, current, not on leave) crew needed to support the planned schedule with safe rostering margins.\n2. CURRENT: The headcount that exists on the payroll today.\n3. AVAILABLE: The headcount that is actually operationally available (current - leave - training - medical - IRROPS).\n\nThe gap between REQUIRED and AVAILABLE is the true staffing deficit. Carriers often mistake CURRENT headcount for availability, leading to chronic rostering violations."
      },
    ]
  },

  // ── 10. Ground Ops & Back Office ────────────────────────────────────────────
  {
    id: "ground-ops", title: "Ground Ops & Back Office", icon: Building, color: "#84cc16",
    sections: [
      {
        id: "go-purpose", heading: "Module Purpose",
        content: "Ground Ops and Back Office extends FRMS monitoring to non-flight staff. Ground handling (ramp, cargo, fuelling, towing, passenger services) and back-office personnel (HR, payroll, dispatch) face the same shift-work fatigue risks as flight crew but are typically excluded from all FRMS implementations. Fatigue Shield is one of the first Indian aviation platforms to address this gap."
      },
      {
        id: "go-roles", heading: "Staff Categories Monitored", type: "table",
        headers: ["Category", "Key Fatigue Risks"],
        rows: [
          ["Ramp / Ground Handling", "Night shifts, heavy equipment operation, high-noise environment, physical exertion"],
          ["Cargo Operations", "Manual handling, hazmat exposure, irregular shift patterns"],
          ["Aircraft Fuelling", "Safety-critical task requiring full concentration — night shift fatigue is a significant risk"],
          ["Aircraft Towing", "Complex vehicle operation near aircraft with minimal error margin"],
          ["Passenger Services", "Irregular hours during IRROPS, physical and emotional stress"],
          ["Dispatch / Ops Control", "Critical decision-making role — often works extended hours during IRROPS"],
        ]
      },
    ]
  },

  // ── 11. Abbreviations ───────────────────────────────────────────────────────
  {
    id: "abbreviations", title: "Abbreviations & Glossary", icon: FileText, color: "#64748b",
    sections: [
      {
        id: "abbr-list", heading: "Full Glossary", type: "table",
        headers: ["Abbreviation", "Full Form", "Definition"],
        rows: [
          ["FRMS", "Fatigue Risk Management System", "Structured approach to managing crew fatigue risk to an acceptable level (ICAO Doc 9966)"],
          ["FDP", "Flight Duty Period", "Total time from report-on-duty to end of last flight. DGCA domestic max: 13 hours"],
          ["FTL", "Flight Time Limitations", "Maximum flying hours per period (e.g. 900h/year, 100h/28 days, 60h/7 days)"],
          ["FDTL", "Flight Duty Time Limitations", "DGCA India's overarching framework combining FTL and rest requirements (CAR Section 7 Series M)"],
          ["DGCA", "Directorate General of Civil Aviation", "India's civil aviation regulatory authority"],
          ["CAR", "Civil Aviation Requirements", "DGCA regulatory publications (e.g. CAR Section 7 Series M — Crew Scheduling)"],
          ["WOCL", "Window of Circadian Low", "0200–0559 local time — human alertness at physiological minimum. Duty in WOCL adds fatigue score penalty"],
          ["ARC", "Airworthiness Review Certificate", "Annual certificate confirming aircraft continued airworthiness (CAR-M). Required for revenue operations"],
          ["MEL", "Minimum Equipment List", "Approved list of items that may be inoperative for dispatch — categorised A/B/C/D by deferral period"],
          ["AMP", "Aircraft Maintenance Programme", "CAMO-approved schedule of all required maintenance tasks and their intervals"],
          ["CAMO", "Continuing Airworthiness Management Organisation", "Entity managing ongoing aircraft airworthiness obligations"],
          ["SMS", "Safety Management System", "Systematic process for managing safety risks and promoting a safety culture (DGCA CAR-19)"],
          ["IRROPS", "Irregular Operations", "Any deviation from the published schedule — weather delay, AOG, diversion, crew sick, etc."],
          ["FO", "First Officer", "Second-in-command pilot (right-hand seat). Requires CPL + type rating"],
          ["AME", "Aircraft Maintenance Engineer", "Licensed engineer authorised to certify maintenance work and issue Certificate of Release to Service"],
          ["AOG", "Aircraft on Ground", "Aircraft grounded due to unserviceability — cannot operate revenue flights"],
          ["SEP", "Safety and Emergency Procedures", "Mandatory annual recurrent training for all crew on evacuation, fire, and emergency equipment"],
          ["CRM", "Crew Resource Management", "Training on crew communication, situational awareness, decision-making, and threat/error management"],
          ["DGR", "Dangerous Goods Regulations", "IATA rules for the transport of hazardous materials by air (categories, packaging, declaration)"],
          ["SLA", "Service Level Agreement", "Committed response/resolution time target — e.g. disruption resolution SLA: <60 seconds"],
          ["ATPL", "Airline Transport Pilot Licence", "Highest pilot licence — required for command (Captain) of commercial aircraft"],
          ["CPL", "Commercial Pilot Licence", "Licence required to act as First Officer on commercial operations"],
          ["SAFTE", "Sleep, Activity, Fatigue, and Task Effectiveness", "Biomathematical model of human alertness developed by the US Army Research Lab — concepts used in FRMS scoring"],
          ["ICAO", "International Civil Aviation Organization", "UN agency setting global aviation standards. ICAO Doc 9966 is the FRMS implementation guide"],
          ["ARS", "Airworthiness Review Staff", "Person qualified to conduct ARC airworthiness reviews and issue Airworthiness Review Certificates"],
          ["ATL", "Aircraft Technical Log", "Legal document carried on aircraft recording defects, deferred items, and maintenance actions per flight"],
          ["QP", "IATA airline code", "IATA airline designator code — used in live flight demonstration context"],
          ["IS-BAO", "International Standard for Business Aircraft Operations", "Global safety management framework for business aviation operators"],
        ]
      },
    ]
  },
];

// ─── Content Renderers ───────────────────────────────────────────────────────
function renderSection(section) {
  switch (section.type) {
    case "table":
      return (
        <div className="overflow-x-auto">
          <table className="w-full text-sm border border-border rounded-sm overflow-hidden">
            <thead>
              <tr className="bg-secondary/60">
                {section.headers.map(h => (
                  <th key={h} className="text-left px-4 py-2.5 text-xs font-bold uppercase tracking-wider text-muted-foreground border-b border-border whitespace-nowrap">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {section.rows.map((row, i) => (
                <tr key={i} className={`border-b border-border/50 ${i % 2 === 0 ? "" : "bg-secondary/20"}`}>
                  {row.map((cell, j) => (
                    <td key={j} className={`px-4 py-2.5 text-sm leading-relaxed ${j === 0 ? "font-bold text-foreground" : "text-muted-foreground"}`}>{cell}</td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      );

    case "steps":
      return (
        <div className="space-y-3">
          {section.intro && <p className="text-sm text-muted-foreground leading-relaxed mb-4">{section.intro}</p>}
          {section.steps.map((step, i) => (
            <div key={i} className="flex gap-3">
              <div className="shrink-0 w-6 h-6 rounded-full bg-primary/20 border border-primary/40 flex items-center justify-center text-xs font-bold text-primary mt-0.5">{i + 1}</div>
              <div>
                <div className="font-bold text-foreground text-sm mb-0.5">{step.title}</div>
                <p className="text-sm text-muted-foreground leading-relaxed">{step.body}</p>
              </div>
            </div>
          ))}
        </div>
      );

    case "warning":
      return (
        <div className="flex gap-3 p-4 border border-amber-500/30 bg-amber-500/8 rounded-sm">
          <AlertOctagon size={16} className="text-amber-400 shrink-0 mt-0.5" />
          <div className="text-sm text-amber-200 leading-relaxed whitespace-pre-line">{section.content}</div>
        </div>
      );

    case "note":
      return (
        <div className="flex gap-3 p-4 border border-blue-500/30 bg-blue-500/8 rounded-sm">
          <Info size={16} className="text-blue-400 shrink-0 mt-0.5" />
          <div className="text-sm text-blue-200 leading-relaxed whitespace-pre-line">{section.content}</div>
        </div>
      );

    default:
      return <p className="text-sm text-muted-foreground leading-relaxed">{section.content}</p>;
  }
}

// ─── Main Component ───────────────────────────────────────────────────────────
export default function OperationsManual() {
  const [activeChapter, setActiveChapter] = useState("introduction");
  const [search, setSearch] = useState("");
  const [expandedSections, setExpandedSections] = useState({});
  const contentRef = useRef(null);

  const chapter = CHAPTERS.find(c => c.id === activeChapter);

  // Client-side search — filters chapters/sections by title or text content
  const searchResults = useMemo(() => {
    if (!search.trim()) return [];
    const q = search.toLowerCase();
    const results = [];
    for (const ch of CHAPTERS) {
      for (const sec of ch.sections) {
        const haystack = [
          ch.title, sec.heading,
          sec.content || "",
          sec.intro || "",
          ...(sec.steps || []).map(s => s.title + " " + s.body),
          ...(sec.rows || []).flat(),
        ].join(" ").toLowerCase();
        if (haystack.includes(q)) {
          results.push({ chapter: ch, section: sec });
        }
      }
    }
    return results;
  }, [search]);

  function toggleSection(id) {
    setExpandedSections(p => ({ ...p, [id]: !p[id] }));
  }

  function goToSection(chapterId, sectionId) {
    setActiveChapter(chapterId);
    setSearch("");
    setTimeout(() => {
      const el = document.getElementById(sectionId);
      if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
    }, 100);
  }

  // Expand all sections when chapter changes
  useEffect(() => {
    if (chapter) {
      const expanded = {};
      chapter.sections.forEach(s => { expanded[s.id] = true; });
      setExpandedSections(expanded);
    }
    if (contentRef.current) contentRef.current.scrollTop = 0;
  }, [activeChapter, chapter]);

  return (
    <div className="flex h-[calc(100vh-0px)] overflow-hidden" data-testid="operations-manual">

      {/* ── Left Sidebar ─────────────────────────────────────────────────── */}
      <div className="w-64 shrink-0 border-r border-border bg-card/60 flex flex-col overflow-hidden">
        <div className="px-4 py-4 border-b border-border">
          <div className="flex items-center gap-2 mb-3">
            <BookOpen size={14} className="text-primary" />
            <span className="text-xs font-bold uppercase tracking-widest text-muted-foreground">Operations Manual</span>
          </div>
          <div className="relative">
            <Search size={12} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-muted-foreground" />
            <input
              type="text" placeholder="Search manual..."
              value={search} onChange={e => setSearch(e.target.value)}
              data-testid="manual-search-input"
              className="w-full bg-background border border-border rounded-sm pl-7 pr-3 py-1.5 text-xs focus:border-primary focus:outline-none placeholder:text-muted-foreground/60"
            />
          </div>
        </div>
        <nav className="flex-1 overflow-y-auto py-2">
          {CHAPTERS.map((ch, i) => (
            <button key={ch.id} onClick={() => setActiveChapter(ch.id)} data-testid={`manual-chapter-${ch.id}`}
              className={`w-full flex items-center gap-2.5 px-4 py-2 text-left transition-colors text-xs ${
                activeChapter === ch.id
                  ? "bg-primary/10 border-r-2 border-primary text-primary font-bold"
                  : "text-muted-foreground hover:text-foreground hover:bg-secondary/40"
              }`}>
              <ch.icon size={12} style={{ color: activeChapter === ch.id ? ch.color : undefined }} className="shrink-0" />
              <span className="leading-snug">{ch.title}</span>
            </button>
          ))}
        </nav>
        <div className="px-4 py-3 border-t border-border">
          <button onClick={() => window.print()}
            className="flex items-center gap-1.5 text-xs text-muted-foreground hover:text-foreground w-full">
            <Printer size={11} /> Print / Export PDF
          </button>
        </div>
      </div>

      {/* ── Main Content ────────────────────────────────────────────────── */}
      <div ref={contentRef} className="flex-1 overflow-y-auto bg-background">

        {/* Search Results */}
        {search.trim() && (
          <div className="p-6 space-y-3">
            <div className="text-xs text-muted-foreground font-bold uppercase tracking-widest mb-4">
              {searchResults.length} result{searchResults.length !== 1 ? "s" : ""} for "{search}"
            </div>
            {searchResults.length === 0 && (
              <p className="text-sm text-muted-foreground">No results found. Try different keywords.</p>
            )}
            {searchResults.map((r, i) => (
              <button key={i} onClick={() => goToSection(r.chapter.id, r.section.id)}
                className="w-full text-left p-3 border border-border rounded-sm hover:border-primary/50 hover:bg-secondary/20 transition-colors">
                <div className="text-xs text-muted-foreground mb-0.5">{r.chapter.title}</div>
                <div className="text-sm font-bold text-foreground">{r.section.heading}</div>
              </button>
            ))}
          </div>
        )}

        {/* Chapter Content */}
        {!search.trim() && chapter && (
          <div className="max-w-4xl px-8 py-8">
            {/* Chapter header */}
            <div className="flex items-start gap-4 mb-8 pb-6 border-b border-border">
              <div className="w-10 h-10 rounded-sm flex items-center justify-center shrink-0"
                style={{ background: `${chapter.color}18`, border: `1px solid ${chapter.color}40` }}>
                <chapter.icon size={18} style={{ color: chapter.color }} />
              </div>
              <div>
                <div className="text-xs uppercase tracking-widest text-muted-foreground font-bold mb-1">
                  Fatigue Shield Operations Manual
                </div>
                <h1 className="text-2xl font-black text-foreground tracking-tight">{chapter.title}</h1>
                <div className="text-xs text-muted-foreground mt-1">
                  {chapter.sections.length} sections
                </div>
              </div>
            </div>

            {/* Sections */}
            <div className="space-y-4">
              {chapter.sections.map(section => (
                <div key={section.id} id={section.id} className="border border-border rounded-sm overflow-hidden">
                  <button
                    onClick={() => toggleSection(section.id)}
                    className="w-full flex items-center justify-between px-5 py-3 bg-secondary/30 hover:bg-secondary/50 transition-colors text-left"
                  >
                    <div className="flex items-center gap-2">
                      {section.type === "warning" && <AlertOctagon size={13} className="text-amber-400" />}
                      {section.type === "note" && <Info size={13} className="text-blue-400" />}
                      {section.type === "steps" && <List size={13} className="text-primary" />}
                      {section.type === "table" && <Database size={13} className="text-green-400" />}
                      {!section.type && <FileText size={13} className="text-muted-foreground" />}
                      <span className="font-bold text-sm text-foreground">{section.heading}</span>
                    </div>
                    {expandedSections[section.id]
                      ? <ChevronDown size={14} className="text-muted-foreground shrink-0" />
                      : <ChevronRight size={14} className="text-muted-foreground shrink-0" />
                    }
                  </button>
                  {expandedSections[section.id] && (
                    <div className="px-5 py-4 space-y-3">
                      {renderSection(section)}
                    </div>
                  )}
                </div>
              ))}
            </div>

            {/* Chapter nav */}
            <div className="flex justify-between mt-10 pt-6 border-t border-border">
              {CHAPTERS.findIndex(c => c.id === activeChapter) > 0 ? (
                <button
                  onClick={() => setActiveChapter(CHAPTERS[CHAPTERS.findIndex(c => c.id === activeChapter) - 1].id)}
                  className="flex items-center gap-2 text-xs text-muted-foreground hover:text-foreground">
                  ← Previous Chapter
                </button>
              ) : <div />}
              {CHAPTERS.findIndex(c => c.id === activeChapter) < CHAPTERS.length - 1 ? (
                <button
                  onClick={() => setActiveChapter(CHAPTERS[CHAPTERS.findIndex(c => c.id === activeChapter) + 1].id)}
                  className="flex items-center gap-2 text-xs text-muted-foreground hover:text-foreground">
                  Next Chapter →
                </button>
              ) : <div />}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
